import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import StatsCard from '../components/StatsCard';
import ActivityFeed from '../components/ActivityFeed';
import ProgressBar from '../components/ProgressBar';
import API from '../services/api';
import { IconClipboard, IconCheckCircle, IconClock, IconTrendingUp, IconRoof } from '../components/Icons';
import { isDateOverdue } from '../utils/dateUtils';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, activityRes, projectsRes] = await Promise.all([
          API.get('/dashboard/stats'),
          API.get('/dashboard/activity'),
          API.get('/dashboard/project-summaries')
        ]);
        setStats(statsRes.data.stats);
        setActivities(activityRes.data.activities || []);
        setProjects(projectsRes.data.projects || []);
      } catch (err) {
        setError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const skyora = projects.find(p => p.code === 'SKY');
  const mayavan = projects.find(p => p.code === 'MAY');

  return (
    <div className="page">
      <Navbar />
      <main className="main-content">
        <div className="page-header">
          <div>
            <h1 className="page-title">Construction Progress Tracker</h1>
            <p className="page-subtitle">Real-time construction progress, milestone tracking, and delay management across all bungalows.</p>
          </div>
        </div>

        {loading && <div className="loading-spinner">Loading dashboard...</div>}
        {error && <div className="error-message">{error}</div>}

        {stats && (
          <div className="stats-grid">
            <StatsCard
              title="Total Bungalows"
              value={stats.total_bungalows || 0}
              color="slate"
              icon={<IconClipboard size={22} />}
              iconBg="#f1f5f9"
              iconColor="#475569"
            />
            <StatsCard
              title="Completed Handover"
              value={stats.completed || 0}
              color="green"
              icon={<IconCheckCircle size={22} />}
              iconBg="#f0fdf4"
              iconColor="#009966"
            />
            <StatsCard
              title="Active Bungalows"
              value={stats.active || 0}
              color="amber"
              icon={<IconTrendingUp size={22} />}
              iconBg="#fffbeb"
              iconColor="#d97706"
            />
            <StatsCard
              title="Not Started Bungalows"
              value={stats.not_started || 0}
              color="purple"
              icon={<IconClock size={22} />}
              iconBg="#eef0f3"
              iconColor="#45556c"
            />
          </div>
        )}

        <div className="dashboard-projects">
          {skyora && (
            <div className="project-summary-card project-card-green">
              <div className="project-summary-header">
                <div className="project-header-left">
                  <div className="project-icon-wrap project-icon-green">
                    <IconRoof size={18} />
                  </div>
                  <h2 className="project-summary-title">Skyora Karjat</h2>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {isDateOverdue(skyora.target_completion_date) && (skyora.completed_plots || 0) < (skyora.registered_plots || 61) && (
                    <span className="project-delayed-badge">⚠ Delayed</span>
                  )}
                  <span className="project-done-badge project-done-green">
                    {skyora.completed_plots || 0}/{skyora.registered_plots || 61} Done
                  </span>
                </div>
              </div>

              <div className="project-summary-label">
                <span>Overall Construction Completion</span>
                <span className="project-summary-percent">{skyora.avg_completion || 0}%</span>
              </div>
              <ProgressBar percentage={skyora.avg_completion || 0} color="#009966" height="10px" />

              <div className="project-totals-row">
                <span className="project-totals-label">Total Bungalows</span>
                <span className="project-totals-value">{skyora.registered_plots || 61} Bungalows</span>
              </div>

              <div className="project-stat-boxes">
                <div className="stat-box stat-box-green">
                  <div className="stat-box-label">COMPLETED</div>
                  <div className="stat-box-value">{skyora.completed_plots || 0}</div>
                </div>
                <div className="stat-box stat-box-outlined">
                  <div className="stat-box-label">ACTIVE</div>
                  <div className="stat-box-value stat-box-val-amber">{skyora.active_plots || 0}</div>
                </div>
                <div className="stat-box stat-box-gray">
                  <div className="stat-box-label">NOT STARTED</div>
                  <div className="stat-box-value">{skyora.not_started_plots || 0}</div>
                </div>
              </div>

              <div className="project-card-footer">
                <Link to="/projects/skyora" className="view-grid-link view-grid-green">Audit Site →</Link>
              </div>
            </div>
          )}

          {mayavan && (
            <div className="project-summary-card project-card-blue">
              <div className="project-summary-header">
                <div className="project-header-left">
                  <div className="project-icon-wrap project-icon-blue">
                    <IconRoof size={18} />
                  </div>
                  <h2 className="project-summary-title">Sahyadri Sylvan Villas</h2>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {isDateOverdue(mayavan.target_completion_date) && (mayavan.completed_plots || 0) < (mayavan.registered_plots || 34) && (
                    <span className="project-delayed-badge">⚠ Delayed</span>
                  )}
                  <span className="project-done-badge project-done-blue">
                    {mayavan.completed_plots || 0}/{mayavan.registered_plots || 34} Done
                  </span>
                </div>
              </div>

              <div className="project-summary-label">
                <span>Overall Construction Completion</span>
                <span className="project-summary-percent">{mayavan.avg_completion || 0}%</span>
              </div>
              <ProgressBar percentage={mayavan.avg_completion || 0} color="#2563eb" height="10px" />

              <div className="project-totals-row">
                <span className="project-totals-label">Total Bungalows</span>
                <span className="project-totals-value">{mayavan.registered_plots || 34} Bungalows</span>
              </div>

              <div className="project-stat-boxes">
                <div className="stat-box stat-box-green">
                  <div className="stat-box-label">COMPLETED</div>
                  <div className="stat-box-value">{mayavan.completed_plots || 0}</div>
                </div>
                <div className="stat-box stat-box-outlined">
                  <div className="stat-box-label">ACTIVE</div>
                  <div className="stat-box-value stat-box-val-amber">{mayavan.active_plots || 0}</div>
                </div>
                <div className="stat-box stat-box-gray">
                  <div className="stat-box-label">NOT STARTED</div>
                  <div className="stat-box-value">{mayavan.not_started_plots || 0}</div>
                </div>
              </div>

              <div className="project-card-footer">
                <Link to="/projects/sylvan-villas" className="view-grid-link view-grid-blue">Audit Site →</Link>
              </div>
            </div>
          )}
        </div>

        <div className="activity-section">
          <ActivityFeed activities={activities} />
        </div>
      </main>
    </div>
  );
}

export default Dashboard;
